import { Link, usePage } from '@inertiajs/react'
import { Building2, ChevronRight, LogOut, Users } from 'lucide-react'
import { Toaster } from 'sonner'
import { resetPostHog } from '@/lib/posthog'
import type { PageProps, Role } from '@/types'

interface OrganizationOption {
  id: number
  name: string
  slug: string
  role: Role
  businesses_count: number
}

interface Props extends PageProps {
  organizations: OrganizationOption[]
}

const roleLabels: Record<Role, string> = {
  owner: 'Propietario',
  admin: 'Administrador',
  staff: 'Personal',
}

export default function SelectOrganization({ organizations }: Props) {
  const { current_user, impersonation } = usePage<PageProps>().props

  return (
    <div className="min-h-screen bg-background">
      {impersonation?.active && (
        <div className="border-b border-amber-300 bg-amber-100 px-4 py-2 text-sm text-amber-900">
          <div className="mx-auto flex max-w-4xl items-center justify-between gap-3">
            <span>Impersonación iniciada por {impersonation.impersonator_name}.</span>
            <Link
              href="/impersonation"
              method="delete"
              as="button"
              className="rounded bg-amber-900 px-2 py-1 text-xs font-medium text-amber-100"
            >
              Salir
            </Link>
          </div>
        </div>
      )}

      <main className="mx-auto flex min-h-[calc(100vh-41px)] max-w-4xl flex-col px-5 py-10 sm:justify-center sm:py-16">
        <div className="mb-8 flex items-start justify-between gap-4">
          <div>
            <p className="mb-2 text-sm font-medium text-primary">Tippy</p>
            <h1 className="text-3xl font-semibold tracking-tight">Elige una organización</h1>
            <p className="mt-2 text-muted-foreground">
              Hola, {current_user?.name}. Selecciona el negocio que quieres administrar.
            </p>
          </div>
          <Link
            href="/users/sign_out"
            method="delete"
            as="button"
            onClick={resetPostHog}
            className="flex items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
          >
            <LogOut className="h-4 w-4" />
            <span className="hidden sm:inline">Cerrar sesión</span>
          </Link>
        </div>

        {organizations.length > 0 ? (
          <div className="grid gap-3 sm:grid-cols-2">
            {organizations.map((organization) => (
              <Link
                key={organization.id}
                href={`/${organization.slug}/${organization.role === 'staff' ? 'calendar' : 'dashboard'}`}
                className="group flex min-h-32 items-center gap-4 rounded-xl border border-border bg-card p-5 shadow-sm transition-all hover:-translate-y-0.5 hover:border-primary/50 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                  <Building2 className="h-6 w-6" />
                </div>
                <div className="min-w-0 flex-1">
                  <h2 className="truncate text-lg font-semibold">{organization.name}</h2>
                  <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground">
                    <span>{roleLabels[organization.role]}</span>
                    <span className="flex items-center gap-1">
                      <Users className="h-3.5 w-3.5" />
                      {organization.businesses_count}{' '}
                      {organization.businesses_count === 1 ? 'sucursal' : 'sucursales'}
                    </span>
                  </div>
                </div>
                <ChevronRight className="h-5 w-5 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-1 group-hover:text-primary" />
              </Link>
            ))}
          </div>
        ) : (
          <div className="rounded-xl border border-dashed border-border bg-card p-8 text-center">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl bg-muted">
              <Building2 className="h-6 w-6 text-muted-foreground" />
            </div>
            <h2 className="mt-4 text-lg font-semibold">No tienes organizaciones disponibles</h2>
            <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">
              Pide a la persona administradora que te invite o contacta a soporte si esperabas ver un negocio aquí.
            </p>
          </div>
        )}
      </main>
      <Toaster position="top-right" richColors />
    </div>
  )
}
